import isFunction from "lodash/isFunction";
import upperFirst from "lodash/upperFirst";
import React from "react";
import { useSelector } from "react-redux";

import { Button, type ButtonProps } from "~/components/button";
import { IconClose } from "~/components/icon";
import { SkeletonText } from "~/components/skeleton/text";
import { Truncated } from "~/components/truncated";
import { cx } from "~/tools/class-variance";
import { copyToClipboard } from "~/tools/copy-to-clipboard";
import { px } from "~/tools/px";
import { useNow } from "~/tools/use-now";

import { selectTimestampDate } from "../../../app/state/view/timeline/TimelineSelectors";
import { ScrollingContainer } from "../../_scrolling-container";
import { Timestamp } from "../../timestamp";
import { DONT_AUTO_DISMISS, notification } from "../notification/Notification";
import { StateComponent, type StateComponentProps } from "../states/StateComponent";

import { useExplorationDrawerContext } from "./_exploration-drawer-context";
import { usePreserveExplorationDrawerState } from "./_exploration-drawer-preserve-state-context";
import { Drawer, type DrawerNode } from "./Drawer";
import { useClosestDrawer } from "./use-closest-drawer";

export type ExplorationDrawerLayout = "full" | "compact";

export interface ExplorationDrawerProps<TContent>
	extends Omit<React.ComponentProps<typeof ScrollingContainer<"div">>, "el" | "children"> {
	id?: string;
	"data-testid"?: string;
	displayTimestamp?: boolean;
	when: undefined | TContent;
	headerActions?: DrawerNode<TContent>;
	headerTitle?: DrawerNode<TContent>;
	footer?: DrawerNode<TContent>;
	children?: DrawerNode<TContent>;
	/**
	 * @default full
	 */
	layout?: ExplorationDrawerLayout;
	ref?: React.ForwardedRef<HTMLDivElement>;
	onClose?: VoidFunction;
}

const COMPACT_DRAWER_WIDTH_PX = 520;
const FULL_DRAWER_MAX_WIDTH_PX = 1600;
const DRAWER_PUSH_DISTANCE_PX = 150;

export const ExplorationDrawer: <TContent>(
	props: React.PropsWithRef<ExplorationDrawerProps<TContent>>,
) => React.ReactNode = React.forwardRef(
	(
		{
			className,
			when,
			headerTitle,
			displayTimestamp,
			footer,
			headerActions,
			"data-testid": dataTestId,
			children: renderer,
			layout = "full",
			onClose: onCloseFromProps,
			id,
			...props
		},
		ref,
	): React.ReactElement => {
		const { onClose, children } = useExplorationDrawerContext();

		const { clear, preserve, state: preservedState } = usePreserveExplorationDrawerState<typeof when>(id);

		return (
			<Drawer
				bodyStyle={{
					display: "flex",
					flexDirection: "column",
				}}
				data-testid={dataTestId}
				onClose={(dismissed, state) => {
					if (dismissed) {
						preserve(state);
					} else {
						clear();
					}

					(onCloseFromProps ?? onClose)();
				}}
				placement="right"
				preservedContent={preservedState}
				push={{ distance: DRAWER_PUSH_DISTANCE_PX }}
				when={when}
				width={
					layout === "full"
						? `min(${px(FULL_DRAWER_MAX_WIDTH_PX)}, calc(100% - ${px(2 * DRAWER_PUSH_DISTANCE_PX)}))`
						: COMPACT_DRAWER_WIDTH_PX
				}
			>
				{(content, close, preservedContent) => (
					<>
						<div
							className={cx(
								"flex min-h-16 items-center justify-between gap-2 bg-white",
								layout === "full" && "px-4",
								layout === "compact" && "px-6",
							)}
						>
							{isFunction(headerTitle)
								? headerTitle(content, close, preservedContent)
								: (headerTitle ?? <div className="flex-1" />)}

							<div className="flex items-center gap-4" data-beest-testid="drawer-header-actions">
								{displayTimestamp && <DrawerTimestamp />}

								{isFunction(headerActions) ? headerActions(content, close, preservedContent) : headerActions}

								<CloseDrawerButton data-testid="drawer-header-close-button" icon size="xs" variant="neutral">
									<IconClose />
								</CloseDrawerButton>
							</div>
						</div>

						<ScrollingContainer
							ref={ref}
							{...props}
							className={cx(
								"flex-1 border-grey-300 border-t border-b border-solid bg-grey-100",
								layout === "full" && "p-4",
								layout === "compact" && "p-6",
								className,
							)}
						>
							{isFunction(renderer) ? renderer(content, close, preservedContent) : renderer}
						</ScrollingContainer>

						{footer && (
							<div className="flex justify-end gap-4 bg-white px-6 py-4">
								{isFunction(footer) ? footer(content, close, preservedContent) : footer}
							</div>
						)}

						{children}
					</>
				)}
			</Drawer>
		);
	},
);

const DrawerTimestamp: React.FC = () => {
	const timestamp = useSelector(selectTimestampDate);
	const timestampOrNow = useNow({
		transform: (now) => timestamp ?? now,
	});

	return <Timestamp time={timestampOrNow} />;
};

export const CloseDrawerButton: React.FC<ButtonProps> = ({ children = "Close", className, ...props }) => {
	const drawer = useClosestDrawer();

	if (drawer == null) {
		throw new Error("Expecting <Drawer> in the component tree");
	}

	return (
		<Button onClick={() => drawer.close()} variant="tertiary" {...props} className={cx("uppercase", className)}>
			{children}
		</Button>
	);
};

export const CopyToClipboardDrawerButton: React.FC<{
	content: string;
	contentType?: string;
}> = ({ content, contentType }) => (
	<Button
		className="uppercase"
		onClick={() => {
			const [failure, success] = copyToClipboard(content, contentType);

			if (failure == null) {
				notification.success({ message: success });
			} else {
				notification.error({
					message: failure,
					duration: DONT_AUTO_DISMISS,
				});
			}
		}}
		variant="primary"
	>
		Copy to clipboard
	</Button>
);

export interface DrawerComponentTitleProps
	extends Pick<StateComponentProps, "component" | "displayTypeName" | "typeOfData"> {
	displayEmptyStateComponent?: boolean;
}

export const DrawerComponentTitle: React.FC<DrawerComponentTitleProps> = ({
	component,
	displayEmptyStateComponent = false,
	displayTypeName,
	typeOfData,
}) =>
	component || displayEmptyStateComponent ? (
		<StateComponent component={component} displayName displayTypeName={displayTypeName} typeOfData={typeOfData} />
	) : null;

export interface DrawerTitleProps {
	icon?: React.ReactNode;
	title: undefined | string;
	typeOfData?: string;
}

export const DrawerTitle: React.FC<DrawerTitleProps> = ({ icon, title, typeOfData }) => (
	<div className="grid max-w-full grid-cols-[auto_auto_auto_auto] items-center gap-4 font-medium text-base text-grey-900 leading-none">
		{icon}

		<SkeletonText
			className="col-span-3 grid skeleton:w-96 skeleton:min-w-1/3 skeleton:max-w-full grid-cols-subgrid skeleton:grid-cols-none gap-2"
			el="div"
			randomize
			when={title}
		>
			{(text) => (
				<>
					{text && <Truncated>{text}</Truncated>}

					{typeOfData != null && text.length > 0 && <span>-</span>}

					{typeOfData != null && upperFirst(typeOfData)}
				</>
			)}
		</SkeletonText>
	</div>
);
